// Styles for the handles and for header truncation in a narrowed column —
// active on any table whose root has the `resizable-columns` class.
import './resizableColumns.css'

// Drag-to-resize <th> columns — same working approach already used by
// CashCallManagementTable.vue (see its own enableColumnResize), pulled out
// here so InvoiceViewAccordionTable.vue and ItemizedInvoicesTable.vue can
// share it rather than re-implementing the same DOM wiring. Deliberately
// plain DOM manipulation (not a Vue-reactive column-width model) — a
// resize-handle div is appended directly to each <th>, so the column-width
// CSS rules that style it must live in a global (non-scoped) <style> block;
// scoped CSS's data-v-xxxx attribute only lands on elements Vue itself
// compiles from the template, never on nodes created via
// document.createElement.
export function enableColumnResize(container) {
  const table = container && container.querySelector('table')
  if (!table) return

  const ths = table.querySelectorAll('th')

  ths.forEach((th) => {
    if (th.querySelector('.resize-handle')) return

    const handle = document.createElement('div')
    handle.className = 'resize-handle'

    th.style.position = 'relative'
    th.appendChild(handle)

    let startX, startWidth

    handle.addEventListener('mousedown', (e) => {
      e.stopPropagation()
      e.preventDefault()

      startX = e.pageX
      startWidth = th.offsetWidth
      let didDrag = false

      const onMouseMove = (moveEvt) => {
        didDrag = true
        const newWidth = Math.max(startWidth + (moveEvt.pageX - startX), 40)
        th.style.width = `${newWidth}px`
        th.style.minWidth = `${newWidth}px`
        th.style.maxWidth = `${newWidth}px`

        // Table-wide column width (not just this header cell) — table-
        // layout: fixed derives every column's width from either its
        // <colgroup><col> (when the table has one) or, absent that, from
        // the header row's own cell widths, which is why syncing the <col>
        // here is only needed for tables that render one at all.
        const index = Array.from(th.parentNode.children).indexOf(th)
        const col = table.querySelectorAll('colgroup col')[index]
        if (col) {
          col.style.width = `${newWidth}px`
          col.style.minWidth = `${newWidth}px`
        }
      }

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)

        // A real drag shouldn't also fire the sort-toggle click that
        // bootstrap-vue's sortable <th> normally reacts to on mouseup.
        if (didDrag) {
          th.addEventListener('click', (clickEvt) => {
            clickEvt.stopPropagation()
            clickEvt.preventDefault()
          }, { capture: true, once: true })
        }
      }

      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    }, true)
  })
}

// Takes the handles back out again. The handles are DOM nodes Vue doesn't
// know about, sitting inside <th> elements Vue does manage — so before
// anything changes WHICH columns a table renders (adding/removing/
// reordering header cells), they have to go, or Vue's own patching trips
// over them (insertBefore "NotFoundError"). Call this synchronously before
// the column change, then enableColumnResize() again once it has rendered.
export function removeColumnResize(container) {
  if (!container) return
  container.querySelectorAll('th > .resize-handle').forEach((handle) => handle.remove())
}
